import { useOrderingMessage } from "../useOrderingMessage";
import {
  MenuOutlined,
  MessageOutlined,
  SelectOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { BillElementItemOut, BillOut } from "../../../../api/bills";
import { useUpdateItemServed } from "../../../../hooks/useBills";
import { useServeRule } from "../../../../hooks/useMonitors";
import { getApiErrorMessage } from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { TablesEmptyState } from "./TablesEmptyState";
import { OrderItemRow } from "./OrderItemRow";
import { StatusLegendHint } from "./StatusLegendHint";
import { SummarizedCheckbox } from "./SummarizedCheckbox";
import {
  buildSummarizedRows,
  sortBillElementsForDisplay,
  toDisplayRow,
} from "./billSummaryUtils";
import { OrderBatchStamp } from "./OrderBatchStamp";
import { useBatchWaiterLabels } from "./useBatchWaiterLabels";
import { useDetailsPhoneLayout } from "./layout/useDetailsPhoneLayout";
import { SERVE_STATUS_LABEL_KEYS, serveBlockedUntilOf } from "./serveRule";
import { isKitchenHeld } from "../../../../lib/kitchenHold";
import { KitchenHoldActions } from "./kitchenHold/KitchenHoldActions";
import { KitchenHoldDeadline } from "./kitchenHold/KitchenHoldDeadline";
import { useKitchenHoldActions } from "./kitchenHold/useKitchenHoldActions";
import { useKitchenHoldExpiryRefresh } from "./kitchenHold/useKitchenHoldExpiryRefresh";

interface OrderDetailsProps {
  bill: BillOut | null;
  selectedItemId?: number | null;
  onItemSelect?: (itemId: number | null) => void;
  /** Отваря релсата с маси на тесен екран — подава се от родителя при нужда. */
  onShowTables?: () => void;
  /** Двоен клик на ред — бърза повторна поръчка на продукта (подава product id). */
  onItemReorder?: (productId: number, lunchMenuId?: number | null) => void;
  /** „Обобщено" — държи се от родителя (там е и изборът на ред). На десктоп
   *  чекбоксът е долу вдясно, точно над лентата с бутоните; на телефон — на
   *  първия разделител. */
  summarized: boolean;
  onToggleSummarized: () => void;
  /** „panel" — в тесния панел до схемата (режим „Схема"): рамката е в тесния
   *  вид (един разделител с „Обобщено", без реда долу), по-малък воден знак, а
   *  редовете — като в списъка (статусът пред името, цената на реда на името);
   *  „page" — според екрана. */
  layout?: "page" | "panel";
}

export const OrderDetails = ({
  bill,
  selectedItemId: externalSelectedItemId,
  onItemSelect,
  onShowTables,
  onItemReorder,
  summarized,
  onToggleSummarized,
  layout = "page",
}: OrderDetailsProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isDesktop, isNarrow } = useViewport();
  // Десктоп в „Списък": ширината зависи от релсата с масите вляво (дръжката
  // `RailResizeHandle`) — отстъпите и дългите имена следват нея
  // (`@container/details`), а стеснена под DETAILS_PHONE_LAYOUT_BELOW тази част
  // е изцяло като на телефон (нищо не се крие).
  const fitsRailWidth = layout === "page" && isDesktop;
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null);
  const phoneLayout = useDetailsPhoneLayout(rootElement, fitsRailWidth);
  // Мобилен stack се ползва до първия breakpoint над 1000px (isDesktop = ≥1024),
  // в тесния панел до схемата и в стеснената дясна част. НЕ се казва isNarrow:
  // споделеният isNarrow от useViewport значи <640px.
  const isCompactLayout = layout === "panel" || !isDesktop || phoneLayout;
  // Редовете следват екрана (и стеснената дясна част) — в панела до схемата
  // са като в списъка.
  const isCompactRows = !isDesktop || phoneLayout;
  const sidePadding = isCompactLayout ? "px-3" : "px-[22px] @max-[600px]/details:px-3";
  // Кой е маркирал всяка поръчка — само при двама и повече; в тесния вид
  // (телефон, панелът до схемата, стеснената дясна част) с инициали.
  const waiterOfBatch = useBatchWaiterLabels(bill?.elements ?? [], isCompactLayout);
  const updateItemServedMutation = useUpdateItemServed();
  // От кой статус се сервира (по сервитьорските монитори на станциите на реда
  // и на добавките му); null — от всеки (serveRule.ts). BE прави същата проверка.
  const serveRule = useServeRule();
  // Задържаните поръчки (още не са в кухнята): „Промени" и „Пусни сега" в
  // разделителя на партидата и опресняване, когато срокът изтече (kitchenHold/).
  const kitchenHoldActions = useKitchenHoldActions(bill);
  useKitchenHoldExpiryRefresh(bill?.elements ?? []);
  const heldElementIds = new Set(
    (bill?.elements ?? []).filter(isKitchenHeld).map((element) => element.id),
  );
  // „Сервира се след статус „Издадено““ — подсказката на ред, който още не може да се
  // сервира; null, ако може. Ред от задържана поръчка не се сервира, докато не
  // влезе в кухнята (BE отказва с 409 същото).
  const serveBlockedHintOf = (
    item: BillElementItemOut,
    elementHeld: boolean,
  ): string | null => {
    if (elementHeld) return t("ordering.kitchenHold.serveBlocked");
    const blockedUntil = serveBlockedUntilOf(item, serveRule);
    return blockedUntil
      ? t("ordering.serveAfterStatus", {
          status: t(SERVE_STATUS_LABEL_KEYS[blockedUntil]),
        })
      : null;
  };
  const [internalSelectedItemId, setInternalSelectedItemId] = useState<
    number | null
  >(null);

  const selectedItemId =
    externalSelectedItemId !== undefined
      ? externalSelectedItemId
      : internalSelectedItemId;

  // Дълга сметка се показва от КРАЯ — там е последната поръчка. Скролира се
  // при смяна на масата и когато се появят нови редове (нова поръчка);
  // опресняване без нови редове (сервиране, статус от кухнята, WS) не мърда
  // списъка, за да не дърпа сервитьора, докато го разглежда.
  const listRef = useRef<HTMLDivElement>(null);
  const billId = bill?.id ?? null;
  const itemCount =
    bill?.elements.reduce((sum, el) => sum + el.items.length, 0) ?? 0;
  const previousCountRef = useRef<{ billId: number | null; count: number }>({
    billId: null,
    count: 0,
  });
  useLayoutEffect(() => {
    const previous = previousCountRef.current;
    previousCountRef.current = { billId, count: itemCount };
    const shouldScroll =
      billId !== previous.billId || itemCount > previous.count;
    if (!shouldScroll || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [billId, itemCount]);

  // Избраният ред (вкл. със стрелките ↑ ↓) се довежда във видимата част.
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  useEffect(() => {
    if (selectedItemId == null) return;
    rowRefs.current
      .get(selectedItemId)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedItemId]);

  const toggleItemSelection = (itemId: number) => {
    const newValue = selectedItemId === itemId ? null : itemId;
    if (onItemSelect) onItemSelect(newValue);
    else setInternalSelectedItemId(newValue);
  };

  const handleToggleServed = async (
    billId: number,
    billElementId: number,
    item: BillElementItemOut,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    // Toggle: несервиран ред се сервира от статуса по правилото за станцията му
    // (без сервитьорски монитор — от всеки); преди него — само подсказката.
    // Втори клик връща served=false и редът пак показва ТЕКУЩИЯ кухненски
    // статус (той може междувременно да е мръднал — кухнята не спира да готви).
    const blockedHint = serveBlockedHintOf(item, heldElementIds.has(billElementId));
    if (blockedHint) {
      message.warning(blockedHint);
      return;
    }
    const nextServed = !item.served;
    try {
      // Сервирането е ОТДЕЛНА ос (bill_element_items.served), не кухненски
      // статус — не пипа какво виждат мониторите/шпикерът.
      await updateItemServedMutation.mutateAsync({
        billId,
        billElementId,
        billElementItemId: item.id,
        served: nextServed,
      });
      message.success(
        t(nextServed ? "ordering.itemServed" : "ordering.itemUnserved"),
      );
    } catch (error) {
      // 400 от BE (напр. правилото се е сменило междувременно) носи текста.
      message.error(getApiErrorMessage(error, t("ordering.itemServedError")));
    }
  };

  if (!bill) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <TablesEmptyState
          icon={<SelectOutlined />}
          title={t("ordering.noTableSelected")}
          description={t("ordering.noTableSelectedHint")}
        />
      </div>
    );
  }

  // Днешна партида НЕ носи етикет „Днес" — показва се само часът.
  const formatDate = (dateString: string): string | null => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const same = (d1: Date, d2: Date) =>
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();
    if (same(date, today)) return null;
    if (same(date, yesterday)) return t("common.yesterday");
    return date.toLocaleDateString("bg-BG", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("bg-BG", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatBatchStamp = (dateString: string) => {
    const datePart = formatDate(dateString);
    const timePart = formatTime(dateString);
    return datePart ? `${datePart} · ${timePart}` : timePart;
  };

  // Партиди без редове не се показват; изцяло сторнирана сметка изглежда празна.
  const sortedElements = sortBillElementsForDisplay(bill.elements);

  const hasItems = sortedElements.length > 0;

  // На телефон чекбоксът седи на линията на разделителя, без собствен ред
  const narrowToggle = isCompactLayout && hasItems && (
    <SummarizedCheckbox
      checked={summarized}
      onToggle={onToggleSummarized}
      small
    />
  );

  return (
    <div
      ref={setRootElement}
      className={`h-full flex flex-col text-pos-ink relative overflow-hidden ${
        fitsRailWidth ? "@container/details" : ""
      }`}
    >
      {/* Хедър: маса · обща сума — САМО на телефон при скрита релса с маси
          (иначе дублира избраната карта вляво) */}
      {onShowTables && isCompactLayout && (
        <div className="border-b border-pos-line2 flex items-baseline gap-2.5 px-3 pt-2.5 pb-2">
          <button
            type="button"
            onClick={onShowTables}
            className="w-[30px] h-[30px] rounded-[9px] border border-pos-ctl bg-pos-card grid place-items-center text-pos-accent self-center shrink-0 cursor-pointer"
          >
            <MenuOutlined className="text-sm" />
          </button>
          <span
            title={bill.name}
            className="min-w-0 truncate font-extrabold text-pos-ink text-base"
          >
            {bill.name}
          </span>
          <span className="ml-auto shrink-0 font-pos-mono font-bold text-pos-ink text-[15px]">
            {parseFloat(bill.total_amount).toFixed(2)} EUR
          </span>
        </div>
      )}

      {/* Скрол зоната е в собствен relative wrapper заедно с водния знак */}
      <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Watermark долу-дясно — както иконите в картите на Модули: едва
            доловим, зад редовете (скрол контейнерът е relative и го покрива).
            15cqw — да се събира и в стеснената дясна част (`@container/details`);
            без контейнер (телефон) cqw е ширината на екрана и не пречи. */}
        <span
          aria-hidden
          className={`pointer-events-none select-none absolute bottom-4 right-6 font-extrabold tracking-tight leading-none text-pos-accent/[.07] ${
            layout === "panel" ? "text-[44px]" : "text-[length:min(10vw,140px,15cqw)]"
          }`}
        >
          UnrealSoft
        </span>
        <div
          ref={listRef}
          className={`relative flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pt-3 pb-5 flex flex-col gap-3.5 ${sidePadding}`}
        >
        {!hasItems ? (
          <div className="flex items-center justify-center pt-10">
            <TablesEmptyState
              icon={<ShoppingOutlined />}
              title={t("ordering.noOrdersForTable")}
              description={t("ordering.noOrdersForTableHint")}
            />
          </div>
        ) : isCompactLayout ? (
          /* Телефон: ЕДИН разделител — часът на първата поръчка вляво,
             „Обобщено" + легендата вдясно. На десктоп заглавна секция НЯМА
             (махната по искане) — там часът е в батч разделителя, а
             легендата — на първия от тях. */
          <div className="flex items-center min-w-0 gap-2.5">
            <OrderBatchStamp
              stamp={formatBatchStamp(sortedElements[0].order_time)}
              waiter={waiterOfBatch(sortedElements[0])}
            />
            <span aria-hidden className="flex-1 h-px bg-pos-line2" />
            {narrowToggle}
            <StatusLegendHint />
          </div>
        ) : null}
        {!hasItems ? null : summarized ? (
          /* ОБОБЩЕНО: общ списък без часови разделители; еднаквите редове са
             слети, затова не са избираеми и лентата не сервира. */
          <section>
            <div className="flex flex-col gap-1.5">
              {buildSummarizedRows(sortedElements).map((row) => (
                <OrderItemRow
                  key={row.key}
                  row={row}
                  isCompactLayout={isCompactRows}
                  isSelected={false}
                  onDoubleClick={
                    onItemReorder
                      ? () => onItemReorder(row.item.item_id, row.item.lunch_menu_id)
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        ) : (
          sortedElements.map((element, elementIndex) => {
            // Задържана поръчка (още не е в кухнята): пунктирана черта, „Промени"
            // и „Пусни сега" в разделителя, срокът вляво от редовете (в тесния
            // вид — в разделителя), редовете с пунктирания кръг.
            const heldHold = isKitchenHeld(element) ? element.kitchen_hold : undefined;
            const holdGutter = !!heldHold && !isCompactLayout;
            return (
            <section key={element.id}>
              {/* Батч хедър: дата · час (· кой е маркирал) + коментар chip — за всяка поръчка;
                  на телефон първата е БЕЗ него (часът ѝ е в общия разделител
                  на хедъра), освен ако няма коментар или задържане за показване.
                  Задържаната се ПРЕНАСЯ на втори ред в тесния панел (до „Схема"),
                  не реже срока и бутоните; на телефон — един ред с малки бутони. */}
              {(!isCompactLayout || elementIndex > 0 || element.comment || heldHold) && (
              <div
                className={`flex items-center mb-2 min-w-0 ${
                  !heldHold ? "gap-2.5" : isNarrow ? "gap-1.5" : "gap-2.5 flex-wrap gap-y-1.5"
                }`}
              >
                {(!isCompactLayout || elementIndex > 0) && (
                  <OrderBatchStamp
                    stamp={formatBatchStamp(element.order_time)}
                    waiter={waiterOfBatch(element)}
                  />
                )}
                  {/* Коментарът към поръчката е ЧЕРВЕН (danger токените) —
                      да се набива на очи, като коментара към продукт */}
                  {element.comment && (
                    <span className="min-w-0 inline-flex items-start gap-1 bg-pos-danger-bg border border-pos-danger-brd text-pos-danger-ink font-semibold italic rounded-[7px] px-1.5 py-0.5 text-[11px] normal-case">
                      <MessageOutlined className="text-[10px] shrink-0 mt-[3px]" />
                      <span className="min-w-0 break-words">
                        {element.comment}
                      </span>
                    </span>
                  )}
                {heldHold && isCompactLayout && (
                  <KitchenHoldDeadline hold={heldHold} variant="inline" />
                )}
                {/* На телефон задържаната е на ЕДИН ред — бутоните горе, центрирани
                    по чертата; при теснота чертата изчезва първа. */}
                <span
                  className={`flex-1 ${isNarrow && heldHold ? "min-w-0" : "min-w-2"} ${
                    heldHold ? "h-0 border-t border-dashed border-pos-accent/60" : "h-px bg-pos-line2"
                  }`}
                />
                {heldHold && kitchenHoldActions.canAct && (
                  <KitchenHoldActions
                    hold={heldHold}
                    onToggleChange={() => kitchenHoldActions.toggleChanging(element.id)}
                    onCloseChange={kitchenHoldActions.closeChanging}
                    changeOpen={kitchenHoldActions.changingElementId === element.id}
                    onPickTime={(time) => void kitchenHoldActions.pickTime(element.id, time)}
                    onRelease={() => void kitchenHoldActions.release(element.id)}
                    busyAction={
                      kitchenHoldActions.busyElementId === element.id
                        ? kitchenHoldActions.busyAction
                        : null
                    }
                    disabled={kitchenHoldActions.busyElementId !== null}
                    lockedTooltip={kitchenHoldActions.lockedTooltip}
                    iconOnly={isNarrow}
                  />
                )}
                {!isCompactLayout && elementIndex === 0 && <StatusLegendHint />}
              </div>
              )}

              <div className={holdGutter ? "flex gap-2.5" : undefined}>
              {holdGutter && (
                <div className="w-[64px] shrink-0 pt-[9px] whitespace-nowrap">
                  <KitchenHoldDeadline hold={heldHold} variant="stacked" />
                </div>
              )}
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                {element.items.map((item) => (
                  <OrderItemRow
                    key={item.id}
                    rowRef={(rowElement) => {
                      if (rowElement) rowRefs.current.set(item.id, rowElement);
                      else rowRefs.current.delete(item.id);
                    }}
                    row={{ ...toDisplayRow(item), kitchenHeld: !!heldHold }}
                    isCompactLayout={isCompactRows}
                    isSelected={selectedItemId === item.id}
                    onClick={() => toggleItemSelection(item.id)}
                    onDoubleClick={
                      onItemReorder
                        ? () => onItemReorder(item.item_id, item.lunch_menu_id)
                        : undefined
                    }
                    onServe={(e) =>
                      handleToggleServed(bill.id, element.id, item, e)
                    }
                    serveBlockedHint={serveBlockedHintOf(item, !!heldHold)}
                  />
                ))}
              </div>
              </div>
            </section>
            );
          })
        )}
        </div>
      </div>

      {/* Десктоп: „Обобщено" долу вдясно, над лентата — извън нея, за да остане
          цялата лента за бутоните. Редът стои и без поръчки (без чекбокс), за
          да не подскача списъкът; височината съвпада с часовника вляво
          (`TableRail`). */}
      {!isCompactLayout && (
        <div className={`shrink-0 flex h-12 items-center justify-end ${sidePadding}`}>
          {hasItems && (
            <SummarizedCheckbox
              checked={summarized}
              onToggle={onToggleSummarized}
            />
          )}
        </div>
      )}
    </div>
  );
};
